import type { ChatMention } from '../types';
import { spanMatches } from './reconcileMentions';

export type MessageSegment =
  | { kind: 'text'; text: string }
  | { kind: 'mention'; text: string; userId: string };

/**
 * Split a message body into plain runs and mention chips.
 *
 * Validates rather than trusts. `mentions` arrives from three places that can all hand
 * over something stale or malformed: a realtime payload, an IndexedDB entry written
 * before the column existed (so `undefined`), and -- for a message someone else sent --
 * whatever their client chose to write. A span that does not still read `@Name`, or that
 * runs off the end, or that overlaps one already taken, is dropped and its text renders
 * plain. Wrong-but-readable beats a chip pointing at the wrong person.
 *
 * The no-mention path returns a single segment after one Array.isArray check: this runs
 * per bubble per render on threads that mostly have no mentions at all.
 */
export function segmentMessageContent(
  content: string,
  mentions: ChatMention[] | null | undefined,
): MessageSegment[] {
  if (!Array.isArray(mentions) || mentions.length === 0) {
    return [{ kind: 'text', text: content }];
  }

  const valid = mentions
    .filter(
      (m) =>
        m != null &&
        typeof m.user_id === 'string' &&
        m.user_id.length > 0 &&
        Number.isInteger(m.start) &&
        Number.isInteger(m.length) &&
        m.start >= 0 &&
        m.length > 0 &&
        m.start + m.length <= content.length &&
        spanMatches(content, m),
    )
    .sort((a, b) => a.start - b.start);

  const segments: MessageSegment[] = [];
  let cursor = 0;

  for (const mention of valid) {
    // Overlapping spans: first one wins, the rest render as ordinary text.
    if (mention.start < cursor) continue;
    if (mention.start > cursor) {
      segments.push({ kind: 'text', text: content.slice(cursor, mention.start) });
    }
    segments.push({
      kind: 'mention',
      text: content.slice(mention.start, mention.start + mention.length),
      userId: mention.user_id,
    });
    cursor = mention.start + mention.length;
  }

  if (cursor < content.length) {
    segments.push({ kind: 'text', text: content.slice(cursor) });
  }

  return segments.length > 0 ? segments : [{ kind: 'text', text: content }];
}
